import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import url from '../../url.js';
import './ui.css';

const ActivitySelectorDialog = ({ open, onClose, onSelectActivity, isLoading, agentName, agentId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activitiesWithPartners, setActivitiesWithPartners] = useState(new Set());
  const [checkingPartners, setCheckingPartners] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUserActivities();
    }
  }, [open]);

  const fetchUserActivities = async () => {
    setLoading(true);
    setError(null);
    setActivitiesWithPartners(new Set()); // 重置狀態
    
    try {
      const jwtToken = localStorage.getItem('jwtToken');
      const userId = localStorage.getItem('userId');
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;

      const response = await axios.get(
        `${url.backendHost}api/activities/myActivity/${userId}`,
        { headers, withCredentials: true }
      );

      const activitiesData = Array.isArray(response.data) ? response.data : [];
      setActivities(activitiesData);

      // 檢查哪些活動已經添加過該夥伴
      if (agentId && activitiesData.length > 0) {
        setCheckingPartners(true);
        await checkExistingPartners(activitiesData, headers);
        setCheckingPartners(false);
      }
    } catch (err) {
      console.error('獲取活動列表失敗:', err);
      setError('無法獲取活動列表，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingPartners = async (activitiesData, headers) => {
    try {
      const activitiesWithPartnerSet = new Set();
      
      // 並行檢查所有活動的夥伴
      const checks = activitiesData.map(async (activity) => {
        try {
          const response = await axios.get(
            `${url.backendHost}api/activities/${activity.id}/partners`,
            { headers, withCredentials: true }
          );
          
          const partners = response.data?.partners || [];
          const hasPartner = partners.some(partner => {
            return String(partner.partnerId) === String(agentId) && partner.isActive;
          });
          
          if (hasPartner) {
            activitiesWithPartnerSet.add(activity.id);
          }
        } catch (err) {
          console.warn(`檢查活動 ${activity.id} 的夥伴失敗:`, err);
        }
      });

      await Promise.all(checks);
      setActivitiesWithPartners(activitiesWithPartnerSet);
    } catch (err) {
      console.error('檢查現有夥伴失敗:', err);
    }
  };

  const handleActivitySelect = (activity) => {
    if (!isLoading && !activitiesWithPartners.has(activity.id)) {
      onSelectActivity(activity);
    }
  };

  if (!open) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content activity-selector-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>選擇活動</h3>
          <p className="dialog-subtitle">將「{agentName}」添加到以下活動中：</p>
          <button className="dialog-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-body">
          {loading || checkingPartners ? (
            <div className="loading-state">
              <Loader2 size={24} className="animate-spin" />
              <p>{loading ? '載入活動列表中...' : '檢查夥伴狀態中...'}</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button className="retry-btn" onClick={fetchUserActivities}>
                重新載入
              </button>
            </div>
          ) : activities.length === 0 ? (
            <div className="empty-state">
              <Calendar size={48} />
              <p>您還沒有創建任何活動</p>
              <span>先創建一個活動，然後就可以添加夥伴了</span>
            </div>
          ) : (
            <div className="activities-list">
              {activities.map((activity) => {
                const hasPartner = activitiesWithPartners.has(activity.id);
                return (
                  <div
                    key={activity.id}
                    className={`activity-item ${hasPartner ? 'activity-item-disabled' : ''}`}
                    onClick={() => handleActivitySelect(activity)}
                  >
                    <div className="activity-info">
                      <h4 className="activity-title">{activity.title}</h4>
                      <p className="activity-description">
                        {activity.info?.settings?.description || activity.content || ''}
                      </p>
                      <div className="activity-meta">
                        <span className="activity-date">
                          <Calendar size={14} />
                          {new Date(activity.createdAt).toLocaleDateString('zh-TW')}
                        </span>
                        <span className="activity-groups">
                          <Users size={14} />
                          {activity.Groups?.length || 0} 個小組
                        </span>
                      </div>
                    </div>
                    {hasPartner ? (
                      <div className="activity-status">
                        <Check size={16} />
                        <span>已添加</span>
                      </div>
                    ) : isLoading ? (
                      <div className="activity-loading">
                        <Loader2 size={16} className="animate-spin" />
                      </div>
                    ) : (
                      <div className="activity-arrow">→</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dialog-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivitySelectorDialog; 